import React, { useEffect, useState } from "react";
import EntityApi from "../../state/entity-api";
import { configStore, EntityV2Store } from "../../state/CommonState";
import Utils from "../../util/Utils";
import Api from "../../state/Api";
import { loader } from "../../util/loader";
import { excludedDocumentNames } from "../../util/constants";

export default function EntityDocumentUpload(props) {
    const { entityId, type, viewOnly = true } = props
    const Entity = EntityV2Store.EntityRec;
    const oldDocuments = Entity.documents;
    const [configs, setConfigs] = useState([]);
    const [files, setFiles] = useState({});
    const [expiryDates, setExpiryDates] = useState({});
    const [selectedMissingIndex, setSelectedMissingIndex] = useState("");
    const [previewDoc, setPreviewDoc] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const today = new Date().toISOString().split("T")[0];

    useEffect(() => {
        fetchConfigs();
    }, []);

    const handleRemoveDocument = async (index) => {
        loader.show();
        try {
            const updatedConfigs = [...configs];

            updatedConfigs[index] = {
                ...updatedConfigs[index],
                fileName: "",
                fileLink: "",
                pkey: "",
                expiryDate:
                    updatedConfigs[index].ExpiryRequired === "Yes"
                        ? null
                        : updatedConfigs[index].expiryDate
            };

            setConfigs(updatedConfigs);
            Entity.docs = updatedConfigs;

            setFiles((prev) => {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            });

            setExpiryDates((prev) => {
                const updated = { ...prev };

                if (updatedConfigs[index].ExpiryRequired === "Yes") {
                    delete updated[index];
                }

                return updated;
            });
        } finally {
            loader.hide();
        }
    };

    const handlePreview = (doc) => {
        setPreviewDoc(doc);
        setShowPreviewModal(true);

        const extension = getFileExtension(doc.fileLink || doc.fileName);
        if (extension === "pdf") {
            loader.show();
        }
    };

    const closePreviewModal = () => {
        loader.hide();
        setShowPreviewModal(false);
        setPreviewDoc(null);
    };

    const getFileExtension = (filename) => {
        if (!filename) return "";
        const parts = filename.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() : "";
    };

    const fetchConfigs = async () => {
        try {
            loader.show()
            const res = await EntityApi.filesbyentity(entityId || 'new');

            let data = [];
            if (res?.allFields) data = res.allFields;
            else if (Array.isArray(res)) data = res;
            let filtered = data.filter(
                (doc) => doc.Type === "All" || doc.Type === type
            );

            if (oldDocuments && Array.isArray(oldDocuments)) {
                const matchedNames = new Set();
                filtered = filtered.map(config => {
                    const configName = config.DocumentName?.DocumentName || config.DocumentName;
                    const existing = oldDocuments.find(d => d.name === configName);
                    if (existing) {
                        matchedNames.add(configName);
                        return { ...config, ...existing, isConfig: true };
                    }
                    return { ...config, isConfig: true };
                });

                oldDocuments.forEach(doc => {
                    if (
                        !matchedNames.has(doc.name) &&
                        !excludedDocumentNames.includes(doc.name) &&
                        (doc.fileLink || doc.fileName)
                    ) {
                        filtered.push({
                            DocumentName: doc.name,
                            ...doc,
                            ExpiryRequired: "No",
                            isConfig: false
                        });
                    }
                });
            }

            const sorted = filtered.sort((a, b) => {
                const aHasFile = a.fileLink || a.fileName;
                const bHasFile = b.fileLink || b.fileName;
                if (aHasFile && !bHasFile) return -1;
                if (!aHasFile && bHasFile) return 1;
                return 0;
            });

            const withIndex = sorted.map((doc, idx) => ({ ...doc, originalIndex: idx }));

            setConfigs(withIndex);
            Entity.docs = withIndex;

            const initialExpiryDates = {};
            filtered.forEach((doc, index) => {
                if (doc.espirydate || doc.expiryDate) {
                    initialExpiryDates[index] = doc.espirydate || doc.expiryDate;
                }
            });
            setExpiryDates(initialExpiryDates);

        } catch (err) {
            console.error(err);
        } finally {
            loader.hide()
        }
    };
    const handleFileChange = async (index, file) => {
        if (!file) return;

        const doc = configs[index];

        if (doc.ExpiryRequired === "Yes" && !expiryDates[index]) {
            configStore.setModalMessage("Please select expiry date");
            return;
        }

        setFiles((prev) => ({
            ...prev,
            [index]: file
        }));

        const docName = typeof doc.DocumentName === "object" ? doc.DocumentName?.DocumentName : (doc.DocumentName || doc.name);

        try {
            loader.show();
            const result = await uploadFile(file, docName);

            if (result && result.filename) {
                const updatedConfigs = [...configs];
                updatedConfigs[index] = {
                    ...updatedConfigs[index],
                    fileName: file.name,
                    fileLink: result.filename,
                    pkey: result.pkey,
                    expiryDate:
                        doc.ExpiryRequired === "Yes"
                            ? expiryDates[index] || null
                            : null
                };

                setConfigs(updatedConfigs);
                Entity.docs = updatedConfigs;

            }
        } catch (err) {
            console.error(err);
            configStore.setModalMessage(err.message || "Upload failed");
        } finally {
            loader.hide();
        }
    };

    const uploadFile = async (file, key) => {
        if (!file) {
            throw new Error("File not selected");
        }

        const url = `${process.env.REACT_APP_FILE_SERVER}/api/v1/filestore/storefile`;
        const formData = new FormData();
        formData.append('myFile', file);
        formData.append('label', key);
        formData.append('key', entityId);

        const response = await fetch(url, {
            method: 'POST',
            headers: Api.H1(),
            credentials: Utils.shouldSendCredentials(url) ? 'include' : 'omit',
            body: formData
        });

        const data = await response.json();

        if (data.error && data.error !== 'No Error') {
            throw new Error(data.error);
        }

        return {
            label: key,
            filename: data.filename || data.key?.filename,
            pkey: data.pkey || data.key?.pkey,
            message: data.message || "Success"
        };
    };;
    const downloadFile = async (filename, entityId, downloadName) => {
        const url = `${process.env.REACT_APP_FILE_SERVER}/api/v1/filestore/getfile/${entityId}/${filename}`;

        loader.show();
        try {
            const response = await fetch(url, {
                headers: Api.H1(),
                credentials: Utils.shouldSendCredentials(url) ? 'include' : 'omit',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const blob = await response.blob();

            const link = document.createElement('a');

            const blobUrl = URL.createObjectURL(blob);

            link.href = blobUrl;
            link.download = downloadName || filename;

            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);

            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading the file:', error);
        } finally {
            loader.hide();
        }
    };

    const handleDateChange = (index, date) => {
        setExpiryDates((prev) => ({
            ...prev,
            [index]: date
        }));
    };

    const isExpired = (date) => {
        if (!date) return false;

        const inputDate = new Date(date);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    };


    const existingConfigs = configs.filter(doc => doc.fileLink || doc.fileName);
    const missingConfigs = configs.filter(doc => !(doc.fileLink || doc.fileName) && doc.isConfig);

    const selectedDoc = selectedMissingIndex !== "" ? configs[selectedMissingIndex] : null;

    return (
        configs.length > 0 &&
        <div style={{ padding: "20px" }}>
            <h3>Additional Documents</h3>

            <div
                style={{
                    display: "flex",
                    fontWeight: "700",
                    borderBottom: "2px solid #ccc",
                    padding: "8px 0"
                }}
            >
                <div style={{ flex: 3 }}>Document Name</div>
                <div style={{ flex: 2, textAlign: "center" }}>Expiry Date</div>
                <div style={{ flex: 2, textAlign: "center" }}>Actions</div>
                {!viewOnly && (
                    <div style={{ flex: 2, textAlign: "center" }}>Replace</div>
                )}
            </div>

            {existingConfigs.map((doc) => {
                const index = doc.originalIndex;
                const currentDate = expiryDates[index] || doc.expiryDate || doc.espirydate;
                const isDisabled = doc.ExpiryRequired === 'Yes' && (!currentDate || isExpired(currentDate));

                return (
                    <div
                        key={index}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #eee",
                            backgroundColor: isExpired(currentDate)
                                ? "#ffe6e6"
                                : "transparent"
                        }}
                    >
                        <div style={{ flex: 3, fontWeight: "600", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {typeof doc?.DocumentName === "object" ? doc?.DocumentName?.DocumentName : (doc?.DocumentName || doc?.name || "Unnamed")}
                        </div>

                        <div style={{ flex: 2, textAlign: "center", minWidth: 0 }}>
                            {doc?.ExpiryRequired === "Yes" ? (
                                <input
                                    type="date"
                                    disabled={viewOnly}
                                    min={today}
                                    value={currentDate || ""}
                                    onChange={(e) =>
                                        handleDateChange(index, e.target.value)
                                    }
                                    style={{ width: "90%" }}
                                />
                            ) : (
                                "-"
                            )}
                        </div>
                        <div style={{ flex: 2, textAlign: "center", minWidth: 0, display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <button
                                title="View Document"
                                onClick={() => handlePreview(doc)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    padding: "4px 8px",
                                    color: "#0066cc"
                                }}
                                aria-label="View Document"
                            >
                                <i className="fa fa-eye" aria-hidden="true"></i>
                            </button>
                            <button
                                title="Download Document"
                                onClick={() =>
                                    downloadFile(
                                        doc.fileLink || doc.fileName,
                                        doc.pkey || entityId,
                                        doc.fileName
                                    )
                                }
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    padding: "4px 8px",
                                    color: "#0066cc"
                                }}
                                aria-label="Download Document"
                            >
                                <i className="fa fa-download" aria-hidden="true"></i>
                            </button>
                            {!viewOnly && (
                                <button
                                    title="Delete Document"
                                    onClick={() => handleRemoveDocument(index)}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        fontSize: "18px",
                                        padding: "4px 8px",
                                        color: "#cc0000"
                                    }}
                                    aria-label="Delete Document"
                                >
                                    <i className="fa fa-trash" aria-hidden="true"></i>
                                </button>
                            )}
                        </div>

                        {!viewOnly && (
                            <div style={{ flex: 2, textAlign: "center", minWidth: 0, overflow: "hidden", whiteSpace: "nowrap" }}>
                                {doc.isConfig ? (
                                    <input
                                        disabled={isDisabled}
                                        type="file"
                                        onChange={(e) =>
                                            handleFileChange(index, e.target.files[0])
                                        }
                                        style={{
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            pointerEvents: isDisabled ? "none" : "auto",
                                            width: "100%"
                                        }}
                                    />
                                ) : (
                                    "-"
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {missingConfigs.length > 0 && !viewOnly && (
                <div
                    style={{
                        marginTop: "20px",
                        padding: "15px",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9"
                    }}
                >
                    <h4 style={{ marginTop: 0 }}>Upload New Document</h4>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ flex: 3 }}>
                            <select
                                value={selectedMissingIndex}
                                onChange={(e) => setSelectedMissingIndex(e.target.value)}
                                style={{ width: "100%", padding: "5px" }}
                            >
                                <option value="">-- Select Document Type --</option>
                                {missingConfigs.map((doc) => (
                                    <option key={doc.originalIndex} value={doc.originalIndex}>
                                        {typeof doc?.DocumentName === "object" ? doc?.DocumentName?.DocumentName : (doc?.DocumentName || doc?.name)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ flex: 2, textAlign: "center" }}>
                            {selectedDoc?.ExpiryRequired === "Yes" ? (
                                <input
                                    type="date"
                                    min={today}
                                    value={expiryDates[selectedMissingIndex] || ""}
                                    onChange={(e) =>
                                        handleDateChange(selectedMissingIndex, e.target.value)
                                    }
                                    style={{ width: "90%" }}
                                />
                            ) : (
                                "-"
                            )}
                        </div>

                        <div style={{ flex: 4, textAlign: "center" }}>
                            {selectedMissingIndex !== "" && (() => {
                                const currentDate = expiryDates[selectedMissingIndex] || selectedDoc.expiryDate || selectedDoc.espirydate;
                                const isDisabled = selectedDoc.ExpiryRequired === 'Yes' && (!currentDate || isExpired(currentDate));

                                return (
                                    <input
                                        disabled={isDisabled}
                                        type="file"
                                        onChange={(e) => {
                                            handleFileChange(selectedMissingIndex, e.target.files[0]);
                                            setSelectedMissingIndex(""); // Reset after selection
                                        }}
                                        style={{
                                            width: "100%",
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            pointerEvents: isDisabled ? "none" : "auto",
                                        }}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {showPreviewModal && previewDoc && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000
                    }}
                    onClick={closePreviewModal}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            padding: "20px",
                            width: "90%",
                            height: "90%",
                            maxWidth: "1000px",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                            <h3 style={{ margin: 0 }}>
                                {typeof previewDoc?.DocumentName === "object"
                                    ? previewDoc?.DocumentName?.DocumentName
                                    : (previewDoc?.DocumentName || previewDoc?.name || "Document")}
                            </h3>
                            <button
                                onClick={closePreviewModal}
                                style={{
                                    background: "none",
                                    border: "1px solid #ccc",
                                    cursor: "pointer",
                                    padding: "5px 10px",
                                    borderRadius: "4px",
                                    fontSize: "16px"
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ flex: 1, overflow: "auto", border: "1px solid #eee", borderRadius: "4px", marginBottom: "10px" }}>
                            {getFileExtension(previewDoc.fileLink || previewDoc.fileName) === 'pdf' ? (
                                <iframe
                                    src={`${process.env.REACT_APP_FILE_SERVER}/api/v1/filestore/getfile/${previewDoc.pkey || entityId}/${previewDoc.fileLink || previewDoc.fileName}`}
                                    style={{ width: "100%", height: "100%", border: "none" }}
                                    title="PDF Preview"
                                    onLoad={() => loader.hide()}
                                    onError={() => loader.hide()}
                                />
                            ) : (
                                <div style={{ padding: "20px", textAlign: "center" }}>
                                    <p>Preview not available for this file type.</p>
                                    <p style={{ fontSize: "12px", color: "#666" }}>
                                        File: {previewDoc.fileName || previewDoc.fileLink}
                                    </p>
                                    <button
                                        onClick={() => downloadFile(previewDoc.fileLink || previewDoc.fileName, previewDoc.pkey || entityId, previewDoc.fileName)}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#0066cc",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            marginTop: "10px"
                                        }}
                                    >
                                       <i className="fa fa-download" aria-hidden="true"></i>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => downloadFile(previewDoc.fileLink || previewDoc.fileName, previewDoc.pkey || entityId, previewDoc.fileName)}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#0066cc",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                }}
                            >
                                <i className="fa fa-download" aria-hidden="true"></i>
                            </button>
        
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}